/**
 * RecentlyViewedProducts - A custom web component for displaying recently viewed products.
 * Utilizes a swiper-slider for carousel display. Manages product data in local storage and
 * dynamically renders product list based on user interaction.
 *
 * @property {Element} swiper - Reference to the swiper-slider element.
 * @property {Element} swiperWrapper - Reference to the .swiper-wrapper element within swiper-slider.
 * @property {number} maxItemCount - Maximum number of items to store/display in the recently viewed list.
 * @property {string} productHandle - Identifier for the currently viewed product.
 */

const recentlyViewedStoreKey = 'recently-viewed-products';

if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this.swiper = this.querySelector('swiper-slider');

      if (!this.swiper) return;

      this.swiperWrapper = this.swiper.querySelector('.swiper-wrapper');
      this.maxItemCount = this.dataset.maxItemCount;
      this.productHandle = this.dataset.productHandle;

      this.addToRecentlyViewedProduct(this.productHandle, this.maxItemCount);
      this.render();
    }

    /**
     * gets recently viewed product handles
     * @returns Array of strings that contains visited product handles
     */
    getRecentlyViewedProducts() {
      const stored = localStorage.getItem(recentlyViewedStoreKey);
      if (!stored) return [];

      try {
        const parsed = parseJson(stored);
        if (parsed instanceof Array) return parsed;
        else {
          console.error('recently viewed product data format is wrong!');
          localStorage.removeItem(stored);
          return [];
        }
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    /**
     * Adds visited product handles to the locale storage with maxItemCount limit
     * @param {String} handle product handle
     * @param {Number} maxItemCount maximum stored item count. Default 10
     * @returns
     */
    addToRecentlyViewedProduct(handle, maxItemCount = 10) {
      if (!handle) return;

      const items = this.getRecentlyViewedProducts();

      if (items.includes(handle)) return;

      items.unshift(handle);
      if (items.length > maxItemCount) items.length = maxItemCount;

      localStorage.setItem(recentlyViewedStoreKey, JSON.stringify(items));
    }

    /**
     * Gets recently viewed product handles from store and fetches the template html data,
     * Then renders them in the swiper.
     */
    async render() {
      let handles = this.getRecentlyViewedProducts();
      handles = handles.filter((e) => e !== this.productHandle);

      if (handles.length > this.maxItemCount) handles.length = this.maxItemCount;

      const products = await this.getAllProductData(handles);

      if (products.length) {
        this.initSwiper(products);
        this.classList.remove('hidden');
        this.classList.add('block');
      }
    }

    initSwiper(products) {
      products.forEach((product) => {
        if (product) this.swiperWrapper.append(product);
      });

      this.refreshSwiper();
    }

    refreshSwiper() {
      this.swiper.swiperInstance.update();
      this.swiper.swiperInstance.navigation.init();
      this.swiper.swiperInstance.navigation.update();
    }

    async getAllProductData(handles) {
      return Promise.all(handles.map((handle) => this.getProductData(handle)));
    }

    /**
     * Gets the content of product.recently-viewed.liquid template with product handle
     * @param {String} handle product handle
     * @returns recently-viewed element or empty string
     */
    async getProductData(handle) {
      const response = await fetch(`/products/${handle}?view=recently-viewed`);

      if (!response.ok) throw new Error(`Product with handle ${handle} not found!`);

      const text = await response.text();
      const parser = new DOMParser();
      const parsedHtml = parser.parseFromString(text, 'text/html');
      const slideElement = parsedHtml.querySelector('.swiper-slide');

      return slideElement || '';
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
